/**
 * 조직도를 «고르는 부품이 읽는 모양»(`PickerGroup[]`)으로 만든다 — 2026-09-22.
 *
 * ## 왜 새 모듈인가
 *
 * `EntityPicker` 는 트리를 **받아서** 그린다(스스로 부르지 않는다 — 「검색은 받아 둔 트리 안에서
 * 한다」가 그 부품의 요점이다). 그런데 이 레포에 트리를 만들어 주는 것이 없다:
 *
 * ```
 * OrgDirectoryModal      트리를 만들지만 «자기 zustand 스토어»에 담는다(그 화면 전용)
 * lib/org-person-lookup  같은 API 를 부르지만 «userId → 한 명» 색인이라 트리가 아니다
 * ```
 *
 * 그래서 같은 네 API(`api/organization.ts` 가 SSOT)를 불러 **트리**를 만든다. 부르는 API 가
 * 한 곳이라 라우트가 바뀌면 거기서 한 번만 바뀐다.
 *
 * ## ⚠ 이것은 3+N 호출이다 — «필요할 때만» 부른다
 *
 * 팀마다 `getTeam` 을 부른다(멤버를 주는 다른 길이 없다). 팀이 40개면 **43번**이다. 이 레포엔
 * 사용자당 **분당 60회** 버킷이 있어(실측) 화면을 여는 것만으로 429 를 맞을 수 있다. 그래서
 *
 *   · **모듈 캐시** — 한 세션에서 한 번만 부른다(두 번째 열기는 호출 0)
 *   · **게으르게** — 「받는 사람」 단계에 들어갈 때 부른다(회의록을 열 때는 안 부른다)
 *   · 로그아웃하면 비운다 — **조직에서 보이는 범위가 사람마다 다르다**(앞 사람 트리를 물려주지 않는다)
 *
 * ★ 후속 서버 요청: **트리와 멤버를 한 번에 주는 라우트**. 그것이 생기면 이 3+N 은 걷어낸다.
 */
import { getTeam, listAllTeams, listDirectMembers, listDivisions, type UserSummary } from '../api/organization';
import type { PickerGroup, PickerPerson } from '../components/shared/EntityPicker/entity-picker-core';
import { registerSessionCache } from './session-caches';

export interface OrgPickerTree {
  /** 부서 → 팀 → 사람. 부서 없는 팀은 위에 나란히 선다. */
  groups: PickerGroup[];
  /** 어느 팀에도 없는 사람(대표이사 등) — 묶음 밖에 둔다. */
  loose: PickerPerson[];
}

/**
 * 한 사람을 고르는 줄로.
 *
 * ⛔ **쉰 계정은 «고를 수 없게» 잠근다** — 되돌릴 수 없는 보내기에서 퇴사자에게 보내면 그 문서가
 *   닫힌 계정으로 간다. 숨기지 않고 **잠근 채로 보인다**(없으면 「왜 안 보이나」를 묻게 된다).
 */
function toPerson(u: UserSummary, teamName: string | null, divisionName: string | null, isLeader = false): PickerPerson {
  const where = [divisionName, teamName].filter(Boolean).join(' ');
  const what = [u.position, isLeader ? '팀장' : null].filter(Boolean).join(' · ');
  return {
    id: u.id,
    name: u.name,
    // 동명이인은 이 한 줄로 갈린다 — 그래서 소속과 직위를 함께 적는다.
    sub: [where, what].filter((s) => s !== '').join(' · ') || undefined,
    // 화면에는 안 보이지만 검색에 걸린다(이름을 몰라도 사번·메일로 찾는다).
    keywords: [u.email, u.employeeNumber ?? ''].filter(Boolean).join(' '),
    disabled: u.isActive === false,
  };
}

let cache: Promise<OrgPickerTree> | null = null;

async function build(): Promise<OrgPickerTree> {
  const [divisions, teams, direct] = await Promise.all([listDivisions(), listAllTeams(), listDirectMembers()]);

  // 팀 상세(=멤버)는 팀마다 한 번. 한 팀이 실패해도 트리 전체를 버리지 않는다.
  const details = await Promise.all(
    teams.map(async (t) => {
      try {
        return { team: t, detail: await getTeam(t.id) };
      } catch {
        return { team: t, detail: null };
      }
    }),
  );

  const divisionName = (divisionId: string | null): string | null =>
    divisionId === null ? null : divisions.find((d) => d.id === divisionId)?.name ?? null;

  const teamGroup = (d: (typeof details)[number]): PickerGroup => {
    const dn = d.team.divisionName ?? divisionName(d.team.divisionId);
    return {
      id: d.team.id,
      name: d.team.name,
      people: (d.detail?.members ?? []).map((m) => toPerson(m.user, d.team.name, dn, m.isLeader)),
    };
  };

  const groups: PickerGroup[] = divisions
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((div) => ({
      id: div.id,
      name: div.name,
      children: details.filter((d) => d.team.divisionId === div.id).map(teamGroup),
    }));

  // 부서 없는 팀 — 부서 아래로 못 들어가므로 같은 높이에 세운다(안 그리면 통째로 사라진다).
  for (const d of details) {
    if (d.team.divisionId === null) groups.push(teamGroup(d));
  }

  return { groups, loose: direct.map((u) => toPerson(u, null, null)) };
}

/** 트리 한 벌. 같은 세션에서 두 번째 호출은 서버를 부르지 않는다. */
export function loadOrgPickerTree(): Promise<OrgPickerTree> {
  cache ??= build().catch((err: unknown) => {
    // 실패한 약속을 캐시에 남기면 «이 세션 내내» 빈 조직도가 된다 — 비우고 다시 부를 수 있게 한다.
    cache = null;
    throw err;
  });
  return cache;
}

/** 시험 전용 — 모듈 캐시 비우기. */
export function _resetOrgPickerTreeForTests(): void { cache = null; }

registerSessionCache(() => { cache = null; });
